import { inject, Injectable } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import {
  concat,
  concatMap,
  EMPTY,
  Observable,
  of,
  OperatorFunction,
  switchMap,
  tap,
} from 'rxjs';
import { concatLatestFrom } from '@ngrx/operators';

import { Option, Result } from '@sapphire/result';
import { match } from 'ts-pattern';

import { EffectsBase } from '@ps/core-data-access';
import { AuthSelectorsFacade } from '@ps/auth-data-access';
import { UserSettingsSelectorsFacade } from '@ps/user-settings-data-access';
import { ToastService } from '@ps/core-overlay';
import {
  AdvancedFeasibilityFilters,
  canBypassLoiThreshold,
  classifySurvey,
  MC_MESSAGES,
  SURVEY_CLASSIFICATION_KIND,
  Qualification,
  StandardSurveyPayloadInput,
  Survey,
  SurveyKind,
  SurveyMetadataForm,
  SurveyMarginFormState,
  SurveyPricingSnapshot,
} from '@ps/survey-utils';

import {
  CachedEntry,
  resolveQualifications,
  SurveyAudienceForm,
  SurveyLaunchSubmissionForm,
} from '../../utils';

import { SurveyCreationCoreApiActions } from './survey-creation-core.actions';
import {
  actingUser,
  DEFAULT_SAVE_GATE,
  gatherSurveyFormContext,
  isSaveable,
  reportFormErrors,
  SaveGateOptions,
  saveGateForms,
} from './utils/survey-save-pipeline.helper';
import { marginSnapshot } from '../audience/domain/margin-snapshot';
import {
  SurveyPersistenceService,
  SurveyUpdateRequest,
} from '../../services/save/survey-persistence.service';
import { ProjectUpdateInputBuilder } from '../../services/save/project-update-input.builder';
import {
  buildProjectUpdate,
  ProjectUpdateInput,
} from '../audience/utils/project-save.helper';
import { selectSurvey } from './survey-creation-core.selectors';
import { Nullable } from '@ps/utils';

// Same shape once the save gate has confirmed the id — and the form's data with it, so no
// stage downstream has to re-assert that a validated form really does carry one.
interface ValidatedSurveyForSave {
  readonly surveyId: number;
  readonly data: NonNullable<SurveyAudienceForm['data']>;
  readonly launch: NonNullable<SurveyLaunchSubmissionForm['data']>;
  /** Only ever filled behind the `enableTargetMargin` service-operator gate. */
  readonly marginForm: SurveyMarginFormState;
  readonly pricing: SurveyPricingSnapshot;
  readonly advancedFeasibilityFilters: AdvancedFeasibilityFilters;
  readonly qualificationsRecord: Record<string, CachedEntry<Qualification[]>>;
  /** True when the buyer did not ask for this save, so it writes without announcing. */
  readonly silent: boolean;
}

// What a save variant writes beyond the audience form itself.
type SurveyUpdateOverrides = Pick<StandardSurveyPayloadInput, 'status'>;

// Returns trigger actions only — a follow-up's own HTTP runs in its own effect,
// so its failures map to its own failure action, not a save failure.
export type SurveySaveFollowUp = (
  survey: Survey,
  silent: boolean,
) => Observable<Action>;

// Only `saveSurvey` carries the flag; every other save-* action is a buyer
// asking for one, so it reports.
function isSilentSave(trigger: Action): boolean {
  return 'silent' in trigger && trigger.silent === true;
}

/**
 * The save pipeline every save-* effect in SurveyCreationSaveEffects runs:
 * validate → build the request the survey's kind needs → persist → report,
 * then chain the caller's follow-up. Split out of the effects so they keep
 * only what is specific to each save variant.
 */
@Injectable({ providedIn: 'root' })
export class SurveySavePipeline extends EffectsBase {
  private readonly _store = inject(Store);
  private readonly _toastService = inject(ToastService);
  private readonly _persistence = inject(SurveyPersistenceService);
  private readonly _authSelectors = inject(AuthSelectorsFacade);
  private readonly _userSettingsSelectors = inject(UserSettingsSelectorsFacade);
  private readonly _projectUpdateInput = inject(ProjectUpdateInputBuilder);

  // For a save variant whose followUp needs a value carried on the
  // triggering action itself (not produced by the pipeline's own stages,
  // e.g. recalculationsEnabled or the target buyer) — re-emits the same
  // action the pipeline validates against, so followUpFor can close over it
  // per emission instead of the pipeline threading it through.
  public saveWithTrigger<TriggerAction extends Action>(
    followUpFor: (action: TriggerAction) => SurveySaveFollowUp,
  ): OperatorFunction<TriggerAction, Action> {
    return (trigger$) =>
      trigger$.pipe(
        concatMap((action) =>
          of<Action>(action).pipe(this.save(followUpFor(action))),
        ),
      );
  }

  // Runs validate → build → persist for every save-* action. followUp must
  // return trigger actions only — its own HTTP stays in its own effect.
  public save(
    followUp: SurveySaveFollowUp,
    gate: SaveGateOptions = DEFAULT_SAVE_GATE,
    overrides: SurveyUpdateOverrides = {},
  ): OperatorFunction<Action, Action> {
    return (trigger$) =>
      trigger$.pipe(
        this._validateSurveyForSave(gate),
        this._persistSurveyUpdate(followUp, overrides),
      );
  }

  // Stage 1 — gates on an existing, submittable survey, reporting errors if not.
  private _validateSurveyForSave(
    gate: SaveGateOptions,
  ): OperatorFunction<Action, ValidatedSurveyForSave> {
    return (trigger$) =>
      trigger$.pipe(
        gatherSurveyFormContext(this._store),
        tap(({ audienceForm, launchForm, marginForm }) =>
          reportFormErrors(
            this._toastService,
            saveGateForms(audienceForm, launchForm, marginForm, gate),
          ),
        ),
        // One pipeline instead of a filter paired with throwing unwraps: the id and the form's
        // data are only produced where both are known to exist, so the gate and the narrowing
        // cannot drift apart.
        concatMap(
          ({
            trigger,
            surveyId,
            audienceForm,
            launchForm,
            marginForm,
            pricing,
            advancedFeasibilityFilters,
            qualificationsRecord,
          }) =>
            Option.from(surveyId)
              .filter(() =>
                isSaveable(audienceForm, launchForm, marginForm, gate),
              )
              .andThen((validatedSurveyId) =>
                Option.from(audienceForm.data).map((data) => ({
                  surveyId: validatedSurveyId,
                  silent: isSilentSave(trigger),
                  data,
                  launch: launchForm.data,
                  marginForm,
                  pricing,
                  advancedFeasibilityFilters,
                  qualificationsRecord,
                })),
              )
              .match({
                some: (validated) => of(validated),
                none: () => EMPTY,
              }),
        ),
      );
  }

  // Picks the endpoint the survey's kind belongs to. A project writes every
  // child at once and addresses the project row; everything else writes the
  // single active survey.
  // Overrides only reach a standard survey: a project writes its children
  // through the project endpoint, and no project save changes status yet.
  private _updateRequest(
    validated: ValidatedSurveyForSave,
    survey: Nullable<Survey>,
    overrides: SurveyUpdateOverrides,
  ): Observable<Result<SurveyUpdateRequest, string>> {
    return survey && SurveyKind.isProject(survey)
      ? this._projectUpdateRequest(survey)
      : of(Result.ok(this._surveyUpdateRequest(validated, overrides)));
  }

  // The project branch, shared by every project type: the builder reads the
  // project's current state, and the type turns it into its own request.
  private _projectUpdateRequest(
    survey: Survey,
  ): Observable<Result<SurveyUpdateRequest, string>> {
    return of(
      this._projectUpdateInput
        .build({
          noChildrenSelected: MC_MESSAGES.NO_LOCALES_SELECTED,
          childNotSaveable: MC_MESSAGES.LOCALE_NOT_SAVEABLE,
        })
        .map((built) => this._projectRequestFor(survey, built))
        .unwrapOr(Result.err(MC_MESSAGES.LOCALE_NOT_SAVEABLE)),
    );
  }

  // The request's `kind` and its `input` are picked together — the discriminant
  // is what tells SurveyPersistenceService which endpoint the input belongs to,
  // so each project type owns both. Tracker and grouped have no write yet, and
  // `_skipsWrite` keeps them out of this branch until theirs lands.
  private _projectRequestFor(
    survey: Survey,
    context: Omit<ProjectUpdateInput, 'messages'>,
  ): Result<SurveyUpdateRequest, string> {
    return match(classifySurvey(survey).kind)
      .with(SURVEY_CLASSIFICATION_KIND.GROUP_MC, (kind) =>
        buildProjectUpdate({
          ...context,
          messages: {
            noChildrenSelected: MC_MESSAGES.NO_LOCALES_SELECTED,
            childNotSaveable: MC_MESSAGES.LOCALE_NOT_SAVEABLE,
          },
        }).map(
          (input): SurveyUpdateRequest => ({
            kind,
            projectId: context.project.id,
            input,
          }),
        ),
      )
      .otherwise(
        (): Result<SurveyUpdateRequest, string> =>
          Result.err(MC_MESSAGES.LOCALE_NOT_SAVEABLE),
      );
  }

  // Stage 2, standard branch — the exact input updateStandardSurvey expects.
  private _surveyUpdateRequest(
    validated: ValidatedSurveyForSave,
    overrides: SurveyUpdateOverrides,
  ): SurveyUpdateRequest {
    return {
      kind: SURVEY_CLASSIFICATION_KIND.STANDARD,
      surveyId: validated.surveyId,
      input: { ...this._standardSurveyInput(validated), ...overrides },
    };
  }

  private _standardSurveyInput({
    data,
    launch,
    marginForm,
    pricing,
    advancedFeasibilityFilters,
    qualificationsRecord,
  }: ValidatedSurveyForSave): StandardSurveyPayloadInput {
    const user = actingUser(this._authSelectors);
    const { metadata, qualifications, quotas } = data;

    return {
      metadata,
      pricing,
      userId: user.id,
      companyId: user.companyId,
      targeting: {
        qualifications,
        quotas,
        catalogQualifications: this._catalogQualifications(
          metadata,
          qualificationsRecord,
        ),
      },
      advancedFeasibilityFilters,
      enableRespondentStatusFilter:
        !!this._userSettingsSelectors.buyerSettings()
          .enableRespondentStatusFilter,
      launch,
      launchAccess: {
        canBypassLoiThreshold: canBypassLoiThreshold({
          isOperator: this._authSelectors.isOperator(),
          accessLevel: this._authSelectors.userAccessLevel(),
          isFeatureEnabled:
            !!this._userSettingsSelectors.buyerSettings()
              .enableBypassLOIThreshold,
        }),
      },
      ...marginSnapshot(marginForm).mapOr({}, (snapshot) => ({
        margin: snapshot,
      })),
    };
  }

  // Stage 2 — builds the request the survey's kind needs, persists it, reports
  // the outcome, then chains the caller's follow-up. Building and writing share
  // the same `survey`, so they read it once.
  private _persistSurveyUpdate(
    followUp: SurveySaveFollowUp,
    overrides: SurveyUpdateOverrides,
  ): OperatorFunction<ValidatedSurveyForSave, Action> {
    return (context$) =>
      context$.pipe(
        concatLatestFrom(() => this._store.select(selectSurvey)),
        switchMap(([validated, survey]) =>
          this._skipsWrite(survey)
            ? this._announceUnwrittenSave(survey, followUp, validated.silent)
            : this._updateRequest(validated, survey, overrides).pipe(
                switchMap((request) =>
                  request.match({
                    ok: (built) =>
                      this._writeSurveyUpdate(
                        built,
                        followUp,
                        validated.silent,
                        survey,
                      ),
                    err: (error) => this._rejectUnbuildableSave(error),
                  }),
                ),
              ),
        ),
      );
  }

  // Tracker and grouped projects still have no write of their own — their save
  // flow is being sorted out separately. A Group MC is no longer among them: it
  // persists through the project endpoint.
  private _skipsWrite(survey: Nullable<Survey>): survey is Survey {
    return (
      !!survey && SurveyKind.isProject(survey) && !SurveyKind.isGroupMc(survey)
    );
  }

  // Nothing was written, so there is no fresh survey and no save toast — this
  // only announces success so the caller's follow-up still runs.
  private _announceUnwrittenSave(
    survey: Survey,
    followUp: SurveySaveFollowUp,
    silent: boolean,
  ): Observable<Action> {
    return concat(
      of(SurveyCreationCoreApiActions.saveSurveySuccess({ survey })),
      followUp(survey, silent),
    );
  }

  private _rejectUnbuildableSave(error: string): Observable<Action> {
    this._toastService.error(error, { keepOpen: false });
    return of(SurveyCreationCoreApiActions.saveSurveyFailure({ error }));
  }

  private _writeSurveyUpdate(
    request: SurveyUpdateRequest,
    followUp: SurveySaveFollowUp,
    silent: boolean,
    survey: Nullable<Survey>,
  ): Observable<Action> {
    return this._persistence.update(request).pipe(
      tap(() => {
        if (!silent) {
          this._toastService.success('Survey successfully saved', {
            keepOpen: false,
          });
        }
      }),
      switchMap((savedSurvey) =>
        concat(
          of(
            SurveyCreationCoreApiActions.saveSurveySuccess({
              survey: savedSurvey,
            }),
          ),
          followUp(savedSurvey, silent),
        ),
      ),
      this.psCatchError(
        (error) => {
          const message = this._saveErrorMessage(error.message, survey);
          this._toastService.error(message);
          return of(
            SurveyCreationCoreApiActions.saveSurveyFailure({
              error: message,
            }),
          );
        },
        { notify: false },
      ),
    );
  }

  private _saveErrorMessage(message: string, survey: Nullable<Survey>): string {
    if (!survey || !SurveyKind.isTraditionalMc(survey)) {
      return message;
    }

    return `${message} to ${survey.countryName}-${survey.languageName} Survey`;
  }

  // The unfiltered catalog (232 included) — the survey's own qualifications never carry a 232
  // entry (see StandardSurveyTargetingInput.catalogQualifications), so this is read directly off
  // the reference-data selector, the same source the audience qualifications section reads before
  // it strips 232 via prepareVisibleQualifications for display.
  private _catalogQualifications(
    metadata: SurveyMetadataForm,
    qualificationsRecord: Record<string, CachedEntry<Qualification[]>>,
  ): Qualification[] {
    return resolveQualifications(
      qualificationsRecord,
      {
        countryCode: metadata.selectedCountries[0] ?? '',
        languageShortCode: metadata.selectedLanguages[0] ?? '',
      },
      true,
    );
  }
}
